import { motion, useReducedMotion } from "motion/react";
import { Check, X } from "@phosphor-icons/react";
import { whoForCopy } from "../../content";

const easeOut = [0.16, 1, 0.3, 1] as const;

/** Checklist + counter-card: who belongs on the boat, and who really doesn't. */
export function WhoFor() {
  const reduce = useReducedMotion();

  const listItem = (index: number) => ({
    initial: reduce ? false : { opacity: 0, y: 12 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.6, delay: index * 0.06, ease: easeOut },
  });

  return (
    <section className="bg-paper-deep py-24 md:py-36">
      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
        {/* Deliberately asymmetric: wide checklist, small tilted counter-card. */}
        <div className="grid items-start gap-14 lg:grid-cols-[1.2fr_0.8fr] lg:gap-24">
          <div>
            <motion.h2
              initial={reduce ? false : { opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: easeOut }}
              className="display-type max-w-[24ch] text-[clamp(1.8rem,3.4vw,3rem)] text-ink"
            >
              {whoForCopy.yesTitle}
            </motion.h2>

            <ul className="mt-10 grid gap-x-10 gap-y-3 sm:grid-cols-2">
              {whoForCopy.yes.map((item, i) => (
                <motion.li key={item} {...listItem(i)} className="flex items-start gap-3">
                  <Check weight="bold" className="mt-1 size-4 shrink-0 text-ink" aria-hidden />
                  <span className="text-ink-soft">{item}</span>
                </motion.li>
              ))}
            </ul>

            {/* margin scribbles tucked under the checklist */}
            <div className="mt-9 flex flex-wrap gap-x-12 gap-y-3">
              {whoForCopy.asides.map((aside, i) => (
                <motion.p
                  key={aside}
                  {...listItem(i + 2)}
                  className={`hand-note text-ink-soft ${i % 2 === 0 ? "rotate-[-2deg]" : "rotate-[1.5deg] md:mt-3"}`}
                >
                  {aside}
                </motion.p>
              ))}
            </div>
          </div>

          <motion.aside
            initial={reduce ? false : { opacity: 0, y: 28, rotate: 3 }}
            whileInView={{ opacity: 1, y: 0, rotate: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1, ease: easeOut }}
            className="w-full max-w-md self-start rounded-md bg-ink p-8 text-foam shadow-lift lg:mt-16 lg:justify-self-end"
          >
            <h3 className="display-type text-[clamp(1.4rem,2.4vw,2rem)] text-foam">
              {whoForCopy.noTitle}
            </h3>
            <ul className="mt-6 space-y-4">
              {whoForCopy.no.map((item, i) => (
                <motion.li key={item} {...listItem(i)} className="flex items-start gap-3">
                  <X weight="bold" className="mt-1 size-4 shrink-0 text-signal" aria-hidden />
                  <span className="text-foam/80">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
